// Soft-paywall modal state. Triggered by HTTP 402 code:SOFT_PAYWALL —
// never locks the account; Stay on Free always dismisses.
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  SOFT_PAYWALL_EVENT,
  extractSoftPaywall,
  analyticsReasonFromFeature,
} from '../utils/softPaywall';
import { trackPaywallShown } from '../services/conversionEvents';

const SoftPaywallContext = createContext(null);

export const useSoftPaywall = () => {
  const ctx = useContext(SoftPaywallContext);
  if (!ctx) {
    return {
      open: () => false,
      close: () => {},
      isOpen: false,
      payload: null,
    };
  }
  return ctx;
};

export const SoftPaywallProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [payload, setPayload] = useState(null);
  const lastShownKey = useRef(null);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const open = useCallback((raw = {}) => {
    const normalized = extractSoftPaywall(raw) || raw;
    if (!normalized) return false;

    const feature = normalized.feature || normalized.paywall?.feature;
    const reason = normalized.analyticsReason || analyticsReasonFromFeature(feature);
    const enriched = {
      ...normalized,
      feature,
      analyticsReason: reason,
    };

    setPayload(enriched);
    setIsOpen(true);

    // Emit client paywall_shown once per feature open burst
    const key = `${feature || 'unknown'}:${reason || ''}`;
    if (lastShownKey.current !== key) {
      lastShownKey.current = key;
      trackPaywallShown(feature || 'client', {
        toPlan: enriched.paywall?.recommendedPlan,
        reason,
        used: enriched.used,
        limit: enriched.limit,
        currentPlan: enriched.plan || enriched.paywall?.currentPlan,
      });
    }
    return true;
  }, []);

  useEffect(() => {
    const handler = (event) => {
      open(event.detail || {});
    };
    window.addEventListener(SOFT_PAYWALL_EVENT, handler);
    return () => window.removeEventListener(SOFT_PAYWALL_EVENT, handler);
  }, [open]);

  // Reset dedupe when modal closes so a later hit can re-emit
  useEffect(() => {
    if (!isOpen) {
      const t = setTimeout(() => {
        lastShownKey.current = null;
      }, 1500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [isOpen]);

  const value = useMemo(
    () => ({ open, close, isOpen, payload }),
    [open, close, isOpen, payload]
  );

  return (
    <SoftPaywallContext.Provider value={value}>
      {children}
    </SoftPaywallContext.Provider>
  );
};

export default SoftPaywallContext;
